/**
 * Pure helpers for Colo vs Azure migration performance UI (parity, deltas, verdicts).
 */

/** @returns {number|null} */
export function pctDelta(colo, azure) {
  if (colo == null || azure == null || !Number.isFinite(colo) || !Number.isFinite(azure)) return null;
  if (colo === 0) return azure === 0 ? 0 : null;
  return ((azure - colo) / Math.abs(colo)) * 100;
}

/**
 * Parity index with Colo = 100. For "lower is better" metrics: index = (azure/colo)*100 — 100 = same, >100 = Azure worse.
 * For "higher is better": index = (azure/colo)*100 — >100 = Azure better.
 */
export function parityIndexLowerBetter(colo, azure) {
  if (colo == null || azure == null || !Number.isFinite(colo) || !Number.isFinite(azure)) return null;
  if (Math.abs(colo) < 1e-12) return null;
  return (azure / colo) * 100;
}

export function parityIndexHigherBetter(colo, azure) {
  return parityIndexLowerBetter(colo, azure);
}

export function mergeDualSeries(coloPts, azurePts) {
  const map = new Map();
  for (const p of coloPts || []) {
    map.set(p.t, { t: p.t, colo: p.v });
  }
  for (const p of azurePts || []) {
    const row = map.get(p.t) || { t: p.t };
    row.azure = p.v;
    map.set(p.t, row);
  }
  return Array.from(map.values()).sort((a, b) => a.t - b.t);
}

/** Azure minus Colo in ms (servlet/http duration in seconds in → ms out) */
export function buildLatencyDeltaMsSeries(merged) {
  return merged
    .map((row) => {
      if (row.colo == null || row.azure == null || !Number.isFinite(row.colo) || !Number.isFinite(row.azure)) {
        return null;
      }
      return { t: row.t, deltaMs: (row.azure - row.colo) * 1000 };
    })
    .filter(Boolean);
}

/** Colo = 100% parity: (azure/colo)*100 for latency (lower is better: >100 means Azure slower) */
export function buildLatencyParitySeries(merged) {
  return merged
    .map((row) => {
      if (row.colo == null || row.azure == null || !Number.isFinite(row.colo) || !Number.isFinite(row.azure)) {
        return null;
      }
      if (row.colo <= 1e-12) return null;
      return { t: row.t, parity: (row.azure / row.colo) * 100 };
    })
    .filter(Boolean);
}

export function verdictLowerBetter(colo, azure, similarBandPct = 8) {
  const d = pctDelta(colo, azure);
  if (d == null || !Number.isFinite(d)) return { label: '—', tone: 'neutral' };
  if (Math.abs(d) <= similarBandPct) return { label: 'Roughly on par', tone: 'neutral' };
  if (d < 0) return { label: 'Azure lower (better)', tone: 'good' };
  return { label: 'Azure higher (worse)', tone: 'bad' };
}

export function verdictHigherBetter(colo, azure, similarBandPct = 10) {
  const d = pctDelta(colo, azure);
  if (d == null || !Number.isFinite(d)) return { label: '—', tone: 'neutral' };
  if (Math.abs(d) <= similarBandPct) return { label: 'Roughly on par', tone: 'neutral' };
  if (d > 0) return { label: 'Azure higher (better)', tone: 'good' };
  return { label: 'Azure lower (worse)', tone: 'bad' };
}
